// ── useTransactions hook ───────────────────────────────────────────────────
// 거래(Transaction) + 거래처(Partner) Firestore 서브컬렉션 관리
// 경로: users/{uid}/transactions/{txnId}, users/{uid}/partners/{partnerId}
//
// 기존 useFirestore는 단일 문서 패턴이지만, 거래는 1년 1,800건+ 예상이라
// 1MB 단일 문서 한계를 넘어 서브컬렉션 사용.

import { useState, useEffect, useCallback } from 'react';
import {
  collection, doc, addDoc, setDoc, deleteDoc, serverTimestamp,
  query, orderBy, limit as fbLimit, where, onSnapshot, getDocs,
} from 'firebase/firestore';
import { db } from './db';

// ── 거래 (Transactions) ────────────────────────────────────────────────────
export function useTransactions(user, options = {}) {
  const { limitCount = 2000, type = null, startDate = null, endDate = null } = options;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) { setTransactions([]); setLoading(false); return; }
    setLoading(true);

    const txnsRef = collection(db, 'users', user.uid, 'transactions');
    const constraints = [];
    if (type) constraints.push(where('type', '==', type));
    if (startDate) constraints.push(where('date', '>=', startDate));
    if (endDate) constraints.push(where('date', '<=', endDate));
    constraints.push(orderBy('date', 'desc'));
    constraints.push(fbLimit(limitCount));
    const q = query(txnsRef, ...constraints);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setTransactions(list);
        setLoading(false);
      },
      (e) => {
        console.error('transactions load error:', e);
        setError(e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user, limitCount, type, startDate, endDate]);

  // ── CRUD ──
  const addTransaction = useCallback(async (txn) => {
    if (!user) throw new Error('not signed in');
    const ref = collection(db, 'users', user.uid, 'transactions');
    const payload = {
      ...txn,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const docRef = await addDoc(ref, payload);
    return docRef.id;
  }, [user]);

  const updateTransaction = useCallback(async (id, patch) => {
    if (!user) throw new Error('not signed in');
    const ref = doc(db, 'users', user.uid, 'transactions', id);
    await setDoc(ref, { ...patch, updatedAt: serverTimestamp() }, { merge: true });
  }, [user]);

  const deleteTransaction = useCallback(async (id) => {
    if (!user) throw new Error('not signed in');
    const ref = doc(db, 'users', user.uid, 'transactions', id);
    await deleteDoc(ref);
  }, [user]);

  return {
    transactions,
    loading,
    error,
    addTransaction,
    updateTransaction,
    deleteTransaction,
  };
}

// ── 거래처 (Partners) ──────────────────────────────────────────────────────
// 작은 규모(~수백)니까 onSnapshot으로 전체 로드.
export function usePartners(user) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setPartners([]); setLoading(false); return; }
    setLoading(true);

    const ref = collection(db, 'users', user.uid, 'partners');
    const q = query(ref, orderBy('lastUsedAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ id: d.id, ...d.data() }));
        setPartners(list);
        setLoading(false);
      },
      (e) => {
        console.error('partners load error:', e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  const addPartner = useCallback(async (partner) => {
    if (!user) throw new Error('not signed in');
    const ref = collection(db, 'users', user.uid, 'partners');
    const payload = {
      ...partner,
      createdAt: serverTimestamp(),
      lastUsedAt: serverTimestamp(),
    };
    const docRef = await addDoc(ref, payload);
    return docRef.id;
  }, [user]);

  const updatePartner = useCallback(async (id, patch) => {
    if (!user) throw new Error('not signed in');
    const ref = doc(db, 'users', user.uid, 'partners', id);
    await setDoc(ref, { ...patch, lastUsedAt: serverTimestamp() }, { merge: true });
  }, [user]);

  const deletePartner = useCallback(async (id) => {
    if (!user) throw new Error('not signed in');
    const ref = doc(db, 'users', user.uid, 'partners', id);
    await deleteDoc(ref);
  }, [user]);

  // 이름으로 거래처 찾기 (대소문자 무시, 부분일치)
  const findPartnerByName = useCallback((name) => {
    if (!name) return null;
    const target = name.trim().toLowerCase();
    return partners.find(p => (p.name || '').trim().toLowerCase() === target) || null;
  }, [partners]);

  return {
    partners,
    loading,
    addPartner,
    updatePartner,
    deletePartner,
    findPartnerByName,
  };
}

// ── 집계 헬퍼 ──────────────────────────────────────────────────────────────
// 클라이언트 사이드 집계 (서버 함수 만들기 전 임시).
// 거래 건수 ~200건까지는 클라이언트에서 충분.

export function summarizeTransactions(transactions) {
  let income = 0, expense = 0;
  let qualifiedExpense = 0, unqualifiedExpense = 0;
  let presumedSupply = 0;
  const byMajor = {};

  for (const t of transactions) {
    const amount = Number(t.supply) || 0;
    if (t.type === 'income') {
      income += amount;
    } else {
      expense += amount;
      if (t.evidence?.isQualified) qualifiedExpense += amount;
      else unqualifiedExpense += amount;
      if (t.vatTreatment?.presumed) presumedSupply += amount;
      const M = t.category?.major || 'unknown';
      byMajor[M] = (byMajor[M] || 0) + amount;
    }
  }

  return {
    income,
    expense,
    profit: income - expense,
    qualifiedExpense,
    unqualifiedExpense,
    qualifiedRatio: expense > 0 ? qualifiedExpense / expense : 0,
    presumedSupply,
    byMajor,
    count: transactions.length,
  };
}

// 기간 필터
export function filterByPeriod(transactions, year, quarter) {
  if (!year) return transactions;
  return transactions.filter(t => {
    if (!t.date) return false;
    const d = String(t.date);
    const y = parseInt(d.slice(0, 4), 10);
    const m = parseInt(d.slice(5, 7), 10);
    if (y !== year) return false;
    if (!quarter) return true;
    const q = Math.ceil(m / 3);
    return q === quarter;
  });
}
